import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import chatSvgPaths from '../imports/svg-gb9dxauhw0';
import { Participant } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { MetadataViewer } from './MetadataViewer';
import { GroupChatProfile } from './GroupChatProfile';
import { ProfileOverlay } from './ProfileOverlay';
import AnalyticsApiService from '../services/analyticsApi';
import { FlagModal } from './FlagModal';
import { BoostModal } from './BoostModal';
import { AIAnalysisModal } from './AIAnalysisModal';
import { hasRichMetadata } from '../utils/metadataUtils';
import { LazyProfileImage } from './LazyProfileImage';
import { shouldRenderMessageAsImages } from '../utils/messageImages';
import { extractReactionSummary } from '../utils/messageReactions';
import type { ReactionKind } from '../utils/messageReactions';
import { ReactionMenu } from './ReactionMenu';
import { ConversationInteractionTag } from './ConversationInteractionTag';
import { Carousel_002 } from './Carousel_002';

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isOwn: boolean;
  createdAt: Date;
  // For group chats: label sender
  sender?: string;
  senderId?: string;
  senderName?: string;
  messageHandle?: string;
  messageType?: string;
  metadata?: Record<string, any>;
  interactionType?: string;
  feedItemId?: string;
  postId?: string;
  replyId?: string;
  deliveryStatus?: 'sending' | 'sent' | 'failed';
  deliveryError?: string;
}

interface IntegratedConversationItemProps {
  conversation: {
    id: string;
    name: string;
    phoneNumber: string;
    userPhoneNumber: string;
    profileImage?: string;
    username?: string;
    messages: Message[];
    isGroup: boolean;
    lastMessageTime: Date;
    participants?: Participant[];
    unread?: boolean;
    lastReadAt?: Date | null;
  };
  onSendMessage: (conversationId: string, message: string) => void;
  onReactMessage?: (conversationId: string, messageId: string, reaction: ReactionKind) => Promise<void>;
  onLiveMessage?: (conversationId: string, messageData: any) => void;
  onConversationClick?: (conversationId: string) => void;
  onExpandClick?: (conversationId: string) => void;
  onToggleFullHistory?: (conversationId: string, enabled: boolean) => void;
  currentUserId?: string;
  isActiveConversation?: boolean;
  onTypingChange?: (isTyping: boolean) => void;
  onInspectingChange?: (isInspecting: boolean) => void;
  showFullHistory?: boolean;
}





function MessageInput({ 
  onSendMessage, 
  disabled = false, 
  isActiveConversation = true,
  onConversationClick,
  conversationId,
  onTypingChange
}: { 
  onSendMessage: (message: string) => void; 
  disabled?: boolean;
  isActiveConversation?: boolean;
  onConversationClick?: (conversationId: string) => void;
  conversationId?: string;
  onTypingChange?: (isTyping: boolean) => void;
}) {
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700 border-opacity-50 bg-gray-50 dark:bg-gray-800/50 bg-opacity-50">
      <div className="relative flex items-center">
        <div className="flex-1 relative">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onFocus={() => {
              // Auto-activate conversation when user starts typing
              if (!isActiveConversation && onConversationClick && conversationId) {
                onConversationClick(conversationId);
              }
              if (onTypingChange) onTypingChange(true);
            }}
            onBlur={() => { if (onTypingChange) onTypingChange(false); }}
            placeholder={disabled ? "Click to activate conversation" : "iMessage"}
            disabled={disabled}
            className={`w-full px-4 py-3 bg-white dark:bg-gray-800 bg-opacity-80 border border-gray-300 dark:border-gray-600 border-opacity-50 rounded-full focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-opacity-30 focus:border-blue-500 focus:border-opacity-50 transition-all duration-200 font-['SF_Pro:Regular',_sans-serif] text-base pr-12 shadow-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          />
          <button
            type="submit"
            disabled={disabled}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center ${
              disabled ? 'opacity-30 cursor-not-allowed' : ''
            }`}
          >
            <svg className="w-5 h-5" fill="none" preserveAspectRatio="none" viewBox="0 0 27 27">
              <path d={chatSvgPaths.p20df2880} fill="black" opacity="0.45" />
            </svg>
          </button>
        </div>
      </div>
    </form>
  );
}

function MessageBubble({
  message,
  onOpenMetadata,
  senderLabel,
  onOpenReactionMenu,
}: {
  message: Message;
  onOpenMetadata?: (data: Record<string, any>) => void;
  senderLabel?: string;
  onOpenReactionMenu?: (message: Message, event: React.MouseEvent) => void;
}) {
  // isOwn=true means AI friend message -> Blue bubbles, right side
  // isOwn=false means User message -> Gray bubbles, left side
  const { isImage, imageUrls } = shouldRenderMessageAsImages({
    text: message.text,
    messageType: message.messageType,
  });
  const reactionChips = extractReactionSummary(message.metadata);

  const getMessageA11yLabel = () => {
    const direction = message.isOwn ? 'sent' : 'received';
    if (isImage) return `Image message (${direction}). Press Enter to react.`;
    const snippet = String(message.text || '').trim().slice(0, 140);
    return `Message (${direction}): ${snippet || 'empty'}. Press Enter to react.`;
  };

  const getReactionButtonLabel = () => {
    if (isImage) return 'React to image message';
    const snippet = String(message.text || '').trim().slice(0, 80);
    return `React to message: ${snippet || 'empty'}`;
  };

  const handleReactionKeyDown = (event: React.KeyboardEvent) => {
    if (!onOpenReactionMenu) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget as HTMLElement;
    const rect = element.getBoundingClientRect();
    onOpenReactionMenu(
      message,
      {
        preventDefault: () => {},
        stopPropagation: () => {},
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      } as unknown as React.MouseEvent
    );
  };

  if (isImage) {
    return (
      <div className={`flex flex-col ${reactionChips.length > 0 ? 'mb-6' : 'mb-4'} ${message.isOwn ? 'items-end' : 'items-start'}`}>
        <div className={`max-w-[75%] relative ${message.isOwn ? 'mr-1' : 'ml-1'}`}>
          {/* Sender label for group chats on user messages */}
          {!message.isOwn && senderLabel && (
            <div className="-mt-1 mb-1 text-[11px] text-[#8E8E93] dark:text-gray-400 font-medium">
              {senderLabel}
            </div>
          )}

          {/* Media block (no chat bubble background) */}
          <div
            className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-white/70 dark:bg-gray-900/40 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            onContextMenu={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
            onKeyDown={handleReactionKeyDown}
            tabIndex={0}
            aria-label={getMessageA11yLabel()}
          >
            {/* Screen-reader / automation hook to open reaction menu */}
            <button
              type="button"
              className="sr-only"
              onClick={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
              aria-label={getReactionButtonLabel()}
            >
              React
            </button>
            <Carousel_002
              images={imageUrls.map((url, index) => ({
                src: url,
                alt: `Message image ${index + 1}`,
              }))}
              className="w-full"
              showNavigation
              showPagination
              loop
            />

            {message.metadata && hasRichMetadata(message.metadata) && (
              <div className="px-3 py-2 border-t border-gray-200/70 dark:border-gray-700/60">
                <button
                  type="button"
                  onClick={() => onOpenMetadata && onOpenMetadata(message.metadata as Record<string, any>)}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border text-gray-700 dark:text-gray-200 border-gray-300/70 dark:border-gray-700 hover:bg-gray-100/70 dark:hover:bg-gray-800/50 transition-colors"
                  title="View tool call details"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                    <path fillRule="evenodd" d="M12 2.25a9.75 9.75 0 1 0 0 19.5 9.75 9.75 0 0 0 0-19.5Zm.75 6a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0V8.25Zm0 7.5a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z" clipRule="evenodd" />
                  </svg>
                  Tool call
                </button>
              </div>
            )}
          </div>

          {/* iMessage-style reaction overlay */}
          {reactionChips.length > 0 && (
            <div
              className={`pointer-events-none absolute ${message.isOwn ? 'right-2 -bottom-3' : 'left-2 -bottom-3'}`}
              aria-hidden="true"
            >
              <div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200/70 dark:border-gray-700/60 bg-white/95 dark:bg-gray-950/90 shadow px-2 py-1 text-[13px] leading-none">
                {reactionChips.slice(0, 3).map((chip, idx) => (
                  <span key={`${message.id}-reaction-emoji-${idx}`}>{chip.emoji}</span>
                ))}
                {reactionChips.length > 3 && (
                  <span className="ml-1 text-[11px] text-gray-600 dark:text-gray-200">+{reactionChips.length - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Timestamp - Outside and underneath */}
        <div className={`${reactionChips.length > 0 ? 'mt-4' : 'mt-1'} px-1 ${message.isOwn ? 'mr-3' : 'ml-3'}`}>
          <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-[11px] text-[#8E8E93] dark:text-gray-500 tracking-[-0.1px]">
            {message.timestamp}
            {message.isOwn && message.deliveryStatus === 'sending' && (
              <span className="ml-2 text-gray-500 dark:text-gray-400">Sending…</span>
            )}
            {message.isOwn && message.deliveryStatus === 'failed' && (
              <span
                className="ml-2 text-red-600 dark:text-red-400 font-semibold"
                title={message.deliveryError || 'Not delivered'}
              >
                Not delivered
              </span>
            )}
          </p>
          <ConversationInteractionTag
            interactionType={message.interactionType}
            feedItemId={message.feedItemId}
            postId={message.postId}
            replyId={message.replyId}
            metadata={message.metadata}
            className="mt-1"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${reactionChips.length > 0 ? 'mb-6' : 'mb-4'} ${message.isOwn ? 'items-end' : 'items-start'}`}>
      {/* Message Bubble */}
      <div className={`max-w-[75%] relative ${message.isOwn ? 'mr-1' : 'ml-1'}`}>
        <div className={`
          relative px-4 py-2.5 shadow-sm
          ${message.isOwn 
            ? 'bg-[#007AFF] text-white'  // AI Friend -> Blue
            : 'bg-[#E5E5EA] dark:bg-gray-700 text-black dark:text-gray-100'  // User -> Gray
          }
          focus:outline-none focus:ring-2 focus:ring-blue-500/40
        `}
        style={{
          borderRadius: message.isOwn 
            ? '18px 18px 4px 18px' 
            : '18px 18px 18px 4px'
        }}
        onContextMenu={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
        onKeyDown={handleReactionKeyDown}
        tabIndex={0}
        aria-label={getMessageA11yLabel()}
        >
          {/* Screen-reader / automation hook to open reaction menu */}
          <button
            type="button"
            className="sr-only"
            onClick={(event) => onOpenReactionMenu && onOpenReactionMenu(message, event)}
            aria-label={getReactionButtonLabel()}
          >
            React
          </button>
          {/* Sender label for group chats on user messages */}
          {!message.isOwn && senderLabel && (
            <div className="-mt-1 mb-1 text-[11px] text-[#8E8E93] dark:text-gray-400 font-medium">
              {senderLabel}
            </div>
          )}
          <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-[17px] leading-[22px] tracking-[-0.2px]">
            {message.text}
          </p>
          {message.metadata && hasRichMetadata(message.metadata) && (
            <button
              type="button"
              onClick={() => onOpenMetadata && onOpenMetadata(message.metadata as Record<string, any>)}
              className={`mt-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${message.isOwn ? 'text-white/90 border-white/30 hover:bg-white/10' : 'text-gray-700 border-gray-400/50 hover:bg-gray-200/70'} transition-colors`}
              title="View tool call details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                <path fillRule="evenodd" d="M12 2.25a9.75 9.75 0 1 0 0 19.5 9.75 9.75 0 0 0 0-19.5Zm.75 6a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0V8.25Zm0 7.5a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z" clipRule="evenodd" />
              </svg>
              Tool call
            </button>
          )}
          
          {/* Message Tail */}
          <div 
            className={`absolute bottom-0 ${message.isOwn ? 'right-[-1px]' : 'left-[-1px]'}`}
          >
            <svg 
              width="12" 
              height="19" 
              viewBox="0 0 12 19" 
              fill="none"
            >
              {message.isOwn ? (
                <path 
                  d="M0 0v19c6 0 12-4 12-9V0H0z" 
                  fill="#007AFF"
                />
              ) : (
                <path 
                  d="M12 0v19c-6 0-12-4-12-9V0h12z" 
                  className="fill-[#E5E5EA] dark:fill-gray-700"
                />
              )}
            </svg>
          </div>
        </div>

        {/* iMessage-style reaction overlay */}
        {reactionChips.length > 0 && (
          <div
            className={`pointer-events-none absolute ${message.isOwn ? 'right-2 -bottom-3' : 'left-2 -bottom-3'}`}
            aria-hidden="true"
          >
            <div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200/70 dark:border-gray-700/60 bg-white/95 dark:bg-gray-950/90 shadow px-2 py-1 text-[13px] leading-none">
              {reactionChips.slice(0, 3).map((chip, idx) => (
                <span key={`${message.id}-reaction-emoji-${idx}`}>{chip.emoji}</span>
              ))}
              {reactionChips.length > 3 && (
                <span className="ml-1 text-[11px] text-gray-600 dark:text-gray-200">+{reactionChips.length - 3}</span>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Timestamp - Outside and underneath the bubble */}
      <div className={`${reactionChips.length > 0 ? 'mt-4' : 'mt-1'} px-1 ${message.isOwn ? 'mr-3' : 'ml-3'}`}>
        <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-[11px] text-[#8E8E93] dark:text-gray-500 tracking-[-0.1px]">
          {message.timestamp}
          {message.isOwn && message.deliveryStatus === 'sending' && (
            <span className="ml-2 text-gray-500 dark:text-gray-400">Sending…</span>
          )}
          {message.isOwn && message.deliveryStatus === 'failed' && (
            <span
              className="ml-2 text-red-600 dark:text-red-400 font-semibold"
              title={message.deliveryError || 'Not delivered'}
            >
              Not delivered
            </span>
          )}
        </p>
        <ConversationInteractionTag
          interactionType={message.interactionType}
          feedItemId={message.feedItemId}
          postId={message.postId}
          replyId={message.replyId}
          metadata={message.metadata}
          className="mt-1"
        />
      </div>
    </div>
  );
}

function IntegratedConversationItemInner({ 
  conversation, 
  onSendMessage,
  onReactMessage,
  onLiveMessage,
  onConversationClick,
  onExpandClick,
  onToggleFullHistory,
  currentUserId,
  isActiveConversation = false,
  onTypingChange,
  onInspectingChange,
  showFullHistory = false
}: IntegratedConversationItemProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isProfileOverlayOpen, setIsProfileOverlayOpen] = useState(false);
  const [isLiveConnected, setIsLiveConnected] = useState(false);
  const ownerPhone = currentUserId || conversation.userPhoneNumber;

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const previousActiveState = useRef(isActiveConversation);
  const [isMetadataOpen, setIsMetadataOpen] = useState(false);
  const [activeMetadata, setActiveMetadata] = useState<Record<string, any> | null>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  // Track whether the user is "pinned" to the bottom BEFORE message updates land.
  // This avoids the common bug where isAtBottom() becomes false after a new message increases scrollHeight.
  const pinnedToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const lastScrollDirectionRef = useRef<'up' | 'down' | null>(null);
  const lastScrollAtRef = useRef(0);

  const [reactionMenu, setReactionMenu] = useState<{ messageId: string; x: number; y: number } | null>(null);
  const [reactionSending, setReactionSending] = useState(false);
  const [reactionError, setReactionError] = useState<string | null>(null);
  const reactionMenuRef = useRef<HTMLDivElement | null>(null);
  
  // Flag, Boost, and AI Analysis modals
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
  const [isAIAnalysisModalOpen, setIsAIAnalysisModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Get user ID from participants (human participant)
  const humanParticipant = conversation.participants?.find(p => p.role === 'human');
  const vectordbUserId = humanParticipant ? parseInt(humanParticipant.id) : null;

  const scrollToBottom = (forceImmediate = false) => {
    // Only scroll if this conversation is active
    if (isActiveConversation && messagesContainerRef.current) {
      // Scroll within the message container, not the entire page
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: forceImmediate ? 'auto' : 'smooth'
      });
    }
  };

  // Check if user is at bottom of message container
  const isAtBottom = useCallback(() => {
    if (!messagesContainerRef.current) return true;
    const container = messagesContainerRef.current;
    const threshold = 50; // 50px threshold for "at bottom"
    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
  }, []);

  // Handle scroll events within message container
  const handleMessageScroll = useCallback(() => {
    if (isActiveConversation) {
      const container = messagesContainerRef.current;
      if (!container) return;

      // Track scroll direction (used to avoid snapping back to bottom immediately after a user scrolls up)
      const nextTop = container.scrollTop;
      const prevTop = lastScrollTopRef.current;
      if (nextTop < prevTop) {
        lastScrollDirectionRef.current = 'up';
        lastScrollAtRef.current = Date.now();
      } else if (nextTop > prevTop) {
        lastScrollDirectionRef.current = 'down';
        lastScrollAtRef.current = Date.now();
      }
      lastScrollTopRef.current = nextTop;

      const atBottom = isAtBottom();
      pinnedToBottomRef.current = atBottom;
      setShowScrollToBottom(!atBottom);
      if (onInspectingChange) onInspectingChange(!atBottom);
    }
  }, [isActiveConversation, isAtBottom, onInspectingChange]);

  // Handle initial activation - smooth scroll to bottom when conversation becomes active
  useEffect(() => {
    const justActivated = !previousActiveState.current && isActiveConversation;
    
    if (justActivated) {
      // Conversation just became active - smooth scroll to bottom for better UX
      console.log(`📍 Conversation ${conversation.id} activated - smooth scrolling to bottom`);
      
      // Show scroll indicator
      setIsAutoScrolling(true);
      
      // Delay to ensure messages are rendered in DOM
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          
          // Smooth animated scroll to bottom
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth' // Smooth animation instead of instant
          });

          // We intentionally consider the user "pinned" after activation (matches Agent dashboard UX).
          pinnedToBottomRef.current = true;
          setShowScrollToBottom(false);
          lastScrollDirectionRef.current = 'down';
          lastScrollAtRef.current = Date.now();
          
          console.log(`✨ Smooth scroll initiated for conversation ${conversation.id}`);
          
          // Hide scroll indicator after animation completes (~800ms for smooth scroll)
          setTimeout(() => {
            setIsAutoScrolling(false);
          }, 1000);
        }
      }, 150); // Slightly longer delay to ensure full render
      
      if (onInspectingChange) onInspectingChange(false);
    }
    
    // Update previous state
    previousActiveState.current = isActiveConversation;
  }, [isActiveConversation, conversation.id, onInspectingChange]);

  // Handle jump-to-message (from unified search snippet click)
  useEffect(() => {
    if (!isActiveConversation || !messagesContainerRef.current) return;
    const anyWin: any = window as any;
    const jumpTarget = anyWin.__jumpToMessageId;
    if (jumpTarget != null) {
      // Attempt to locate message element and scroll it into view
      const selector = `[data-message-id="${String(jumpTarget)}"]`;
      const el = messagesContainerRef.current.querySelector(selector) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      // Clear the jump target to avoid repeated jumps
      delete anyWin.__jumpToMessageId;
    }
  }, [isActiveConversation, conversation.messages]);

  // Handle new messages - only auto-scroll if user is at bottom
  useEffect(() => {
    if (!isActiveConversation) return;

    const recentlyScrolledUp =
      lastScrollDirectionRef.current === 'up' && Date.now() - lastScrollAtRef.current < 600;
    if (recentlyScrolledUp) return;

    // Only auto-scroll if the user was already pinned to bottom BEFORE the update landed.
    if (pinnedToBottomRef.current) {
      // Small delay to ensure DOM has updated with new messages
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [conversation.messages, isActiveConversation, isAtBottom]);



  const handleSendMessage = (text: string) => {
    onSendMessage(conversation.id, text);
  };

  const openMetadata = (data: Record<string, any>) => {
    setActiveMetadata(data);
    setIsMetadataOpen(true);
  };

  const handleOpenReactionMenu = useCallback(
    (message: Message, event: React.MouseEvent) => {
      if (!onReactMessage) return;
      event.preventDefault();
      event.stopPropagation();
      setReactionError(null);
      setReactionMenu({ messageId: message.id, x: event.clientX, y: event.clientY });
    },
    [onReactMessage]
  );

  const handleSendReaction = useCallback(
    async (reaction: ReactionKind) => {
      if (!onReactMessage || !reactionMenu) return;
      setReactionSending(true);
      setReactionError(null);
      try {
        await onReactMessage(conversation.id, reactionMenu.messageId, reaction);
        setReactionMenu(null);
      } catch (err) {
        setReactionError(err instanceof Error ? err.message : 'Failed to send reaction.');
      } finally {
        setReactionSending(false);
      }
    },
    [conversation.id, onReactMessage, reactionMenu]
  );

  useEffect(() => {
    if (!reactionMenu) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setReactionMenu(null);
    };
    const handlePointerDown = (event: PointerEvent) => {
      const root = reactionMenuRef.current;
      const target = event.target as Node | null;
      if (!root || !target) return;
      if (root.contains(target)) return;
      setReactionMenu(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [reactionMenu]);

  // Subscribe to real-time conversation events ONLY for active conversation
  useEffect(() => {
    // Only subscribe to real-time updates if this conversation is active
    if (!isActiveConversation) {
      setIsLiveConnected(false);
      return;
    }

    const conversationId = conversation.id;
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    
    // Import SSE manager dynamically to avoid circular dependencies
    const setupSubscription = async () => {
      try {
        const { sseManager } = await import('../services/sseConnectionManager');

        if (cancelled) {
          return;
        }
        
        console.log(`🔴 LIVE: Setting up SSE for conversation ${conversationId}`);
        setIsLiveConnected(true);
        
        const localUnsubscribe = sseManager.subscribe(conversationId, (message) => {
          try {
            console.log(`📨 SSE event received for conversation ${conversationId}:`, message.type);
            
            if (message.type === 'message') {
              // Handle incoming message
              if (onLiveMessage) {
                onLiveMessage(conversationId, message.data);
              } else {
                // Fallback to old behavior
                const content = message.data?.message || message.data?.content;
                if (content && typeof content === 'string') {
                  onSendMessage(conversationId, content);
                }
              }
            } else if (message.type === 'user_join') {
              console.log('👤 User joined conversation:', message.data);
            } else if (message.type === 'user_leave') {
              console.log('👋 User left conversation:', message.data);
            } else if (message.type === 'typing') {
              console.log('✍️ User is typing:', message.data);
            }
          } catch (error) {
            console.error('Error handling SSE message:', error);
          }
        });
        unsubscribe = localUnsubscribe;
        
        // Log connection stats
        const stats = sseManager.getStats();
        console.log('📊 SSE Manager Stats:', stats);

        // If we were cancelled while awaiting imports/subscription, immediately clean up.
        if (cancelled) {
          localUnsubscribe();
          unsubscribe = null;
          setIsLiveConnected(false);
        }
        
      } catch (error) {
        console.error('Failed to setup SSE subscription:', error);
        if (!cancelled) {
          setIsLiveConnected(false);
        }
      }
    };

    setupSubscription();

    // Cleanup function
    return () => {
      cancelled = true;
      if (unsubscribe) {
        console.log(`🔵 Cleaning up SSE subscription for conversation: ${conversationId}`);
        unsubscribe();
        unsubscribe = null;
      }
      setIsLiveConnected(false);
    };
  }, [conversation.id, onSendMessage, onLiveMessage, isActiveConversation]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  // Handle conversation click to activate/deactivate
  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Treat these as preventing toggle/close
    const preventToggleZone = target.closest('[data-prevent-toggle]');
    const isInteractiveElement = target.closest('button, input, a, [role="button"]');
    if (preventToggleZone || isInteractiveElement) {
      return; // ignore clicks in protected zones or interactive controls
    }
    if (!onConversationClick) return;
    // Only activate when inactive; do not auto-deactivate on container clicks
    if (!isActiveConversation) {
      onConversationClick(conversation.id);
    }
  }, [onConversationClick, conversation.id, isActiveConversation]);

  return (
    <div 
      className={`flex gap-8 mb-8 transition-all duration-300 ${
        !isActiveConversation ? 'cursor-pointer hover:bg-gray-50/50 dark:hover:bg-gray-800/50 rounded-3xl p-4' : 'p-4'
      } ${isActiveConversation ? 'bg-blue-50/30 dark:bg-blue-950/20 rounded-3xl shadow-lg ring-2 ring-blue-200/50 dark:ring-blue-800/50' : ''}`}
      onClick={handleClick}
    >
      {/* Profile Card Section - Left Side */}
      <div className="w-[400px] flex-shrink-0" data-prevent-toggle>
        {conversation.isGroup ? (
          /* Group Chat - Use GroupChatProfile component */
          <GroupChatProfile
            groupName={conversation.name}
            participants={conversation.participants || []}
            conversationId={parseInt(conversation.id, 10) || 0}
          />
        ) : (
          /* Individual Chat - Use existing profile card logic */
          <div className="relative h-[450px] rounded-3xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 border-opacity-50 bg-white dark:bg-gray-900 bg-opacity-80 backdrop-blur-xl hover:shadow-xl transition-all duration-300 z-0">
            {/* Profile Background Image */}
            <div className="absolute inset-0">
              {conversation.profileImage ? (
                <LazyProfileImage
                  src={conversation.profileImage}
                  alt={conversation.name}
                  className="w-full h-full bg-center bg-cover bg-no-repeat"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center">
                  {/* Profile Image Placeholder with Initials */}
                  <div className="w-32 h-32 rounded-full bg-white bg-opacity-90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                    <span className="text-gray-600 text-4xl font-['SF_Pro_Display:Bold',_sans-serif]">
                      {conversation.name ? conversation.name.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase() : '??'}
                    </span>
                  </div>
                </div>
              )}
              {conversation.unread && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-blue-600 text-white text-[8px] ring-1 ring-dark-blue-600 dark:ring-blue-600" title="Unread">
                  </span>
                </div>
              )}
              {/* Light overlay for better text readability - minimal opacity to preserve image visibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-[0px]" />
            </div>
            
            {/* Profile Badge - Now Clickable */}
            <button
              onClick={() => setIsProfileOverlayOpen(true)}
              className="absolute top-5 left-5 bg-white dark:bg-gray-800 bg-opacity-90 hover:bg-white dark:hover:bg-gray-700 backdrop-blur-sm h-6 rounded-lg px-4 flex items-center transition-all duration-200 group"
            >
              <svg className="w-3 h-3 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 mr-2 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="font-['SF_Pro_Display:Bold',_sans-serif] text-[#787878] dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 text-sm tracking-[-0.2px] transition-colors">
                Profile
              </span>
            </button>

            {/* Phone Number Badge */}
            {/* <div className="absolute top-5 right-5 bg-white dark:bg-gray-800 bg-opacity-90 backdrop-blur-sm h-6 rounded-lg px-4 flex items-center">
              <span className="font-['SF_Pro_Display:Bold',_sans-serif] text-[#787878] dark:text-gray-400 text-sm tracking-[-0.2px]">
                {conversation.userPhoneNumber}
              </span>
            </div> */}

            {/* Bottom content area */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
  

              {/* Name */}
              <div className="text-center">
                <h3 className={`font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-2xl tracking-[-0.3px] drop-shadow-sm ${conversation.unread ? 'text-white' : 'text-white' }`}>
                  {conversation.name}
                </h3>
              </div>
            </div>

            {/* Profile Overlay for Individual Chats */}
            <ProfileOverlay
              participant={(() => {
                // Support both old API (role='human') and new API (role='user' from kind='user')
                const human = (conversation.participants || []).find((p: any) => 
                  p.role === 'human' || p.role === 'user' || p.kind === 'user'
                );
                if (human) {
                  return {
                    id: human.id,
                    name: human.name || conversation.name,
                    phoneNumber: human.phoneNumber || conversation.userPhoneNumber || conversation.phoneNumber,
                    profileImage: human.profileImage || conversation.profileImage,
                    username: human.username,
                    role: 'user'
                  };
                }
                return {
                  id: conversation.id,
                  name: conversation.name,
                  phoneNumber: conversation.userPhoneNumber || conversation.phoneNumber,
                  profileImage: conversation.profileImage,
                  username: conversation.username,
                  role: 'Contact'
                };
              })()}
              isOpen={isProfileOverlayOpen}
              onClose={() => setIsProfileOverlayOpen(false)}
              conversationId={parseInt(conversation.id)}
            />
          </div>
        )}
      </div>

      {/* Chat Interface Section - Right Side */}
      <div className="flex-1 relative">
        {/* Inactive conversation overlay - Modern aesthetic */}
        {!isActiveConversation && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/50 to-blue-50/40 dark:from-gray-900/70 dark:via-gray-800/60 dark:to-blue-950/40 backdrop-blur-md rounded-3xl z-10 flex items-center justify-center transition-all duration-300 cursor-pointer hover:backdrop-blur-lg group"
          >
            <motion.div 
              className="text-center space-y-4 px-6"
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 20 }}
            >
              {/* Animated icon with pulse effect */}
              <motion.div 
                className="relative w-16 h-16 mx-auto"
                whileHover={{ scale: 1.1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
              >
                {/* Outer pulse ring */}
                <motion.div
                  className="absolute inset-0 bg-gray-400/20 dark:bg-gray-500/20 rounded-full"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                />
                {/* Inner gradient circle - black/white for universal contrast */}
                <div className="relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 dark:from-gray-100 dark:to-white rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                  <svg className="w-8 h-8 text-white dark:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
              </motion.div>
              
              {/* Text content */}
              <div className="space-y-1.5">
                <p className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-[600] text-gray-800 dark:text-gray-100 text-xl tracking-tight">
                  Click to activate
                </p>
                <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[400] text-gray-600 dark:text-gray-400 text-sm max-w-xs mx-auto leading-relaxed">
                  Start a conversation with real-time updates
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        <div className="bg-white dark:bg-gray-900 bg-opacity-80 backdrop-blur-xl rounded-3xl shadow-lg border border-gray-200 dark:border-gray-700 border-opacity-50 overflow-hidden h-[450px] flex flex-col transition-all duration-300 hover:shadow-xl">
          {/* Chat Header */}
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 border-opacity-30 bg-gray-50 dark:bg-gray-800/50 bg-opacity-50 flex-shrink-0" data-prevent-toggle>
            <div className="flex items-center justify-between">
              <div className="text-center flex-1 flex items-center justify-center gap-3">
                {/* Live connection indicator */}
                {isActiveConversation && (
                  <div className="relative">
                    <div 
                      className={`w-3 h-3 rounded-full ${isLiveConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} 
                      title={isLiveConnected ? "Live updates active" : "Connecting..."}
                    />
                    {isLiveConnected && (
                      <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30" />
                    )}
                  </div>
                )}
                <h4 className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-medium text-gray-900 dark:text-gray-100 text-xl tracking-[-0.2px]">
                  {conversation.name}
                </h4>
                {isActiveConversation && (
                  <span className={`text-xs px-2 py-1 rounded-full font-['SF_Pro_Text',_-apple-system,_sans-serif] font-medium ${
                    isLiveConnected 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700' 
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-700'
                  }`}>
                    {isLiveConnected ? '🔴 LIVE' : '⏸️ PAUSED'}
                  </span>
                )}
                
                {/* Actions Menu Button */}
                <div className="relative ml-2" ref={menuRef}>
                  <motion.button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setIsMenuOpen(!isMenuOpen);
                    }}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-all duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Actions"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </motion.button>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {isMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                      {/* Expand Chat */}
                      <button
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setIsMenuOpen(false);
                          if (onExpandClick) {
                            onExpandClick(conversation.id);
                          }
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-300"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span className="text-sm font-medium">Expand Chat</span>
                      </button>

                      {/* Divider */}
                      <div className="border-t border-gray-200 dark:border-gray-700" />

                      {/* Show Full History Toggle */}
                      <button
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setIsMenuOpen(false);
                          if (onToggleFullHistory) {
                            onToggleFullHistory(conversation.id, !showFullHistory);
                          }
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 ${
                          showFullHistory 
                            ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1 flex items-center justify-between">
                          <span className="text-sm font-medium">Show Full History</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            showFullHistory 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {showFullHistory ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      </button>

                      {/* Divider */}
                      <div className="border-t border-gray-200 dark:border-gray-700" />

                      {/* AI Analysis */}
                      <button
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setIsMenuOpen(false);
                          setIsAIAnalysisModalOpen(true);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center gap-3 text-purple-600 dark:text-purple-400"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span className="text-sm font-semibold">🧠 AI Analysis</span>
                      </button>

                      {/* Divider */}
                      {vectordbUserId && <div className="border-t border-gray-200 dark:border-gray-700" />}

                      {/* Flag User */}
                      {vectordbUserId && (
                        <button
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setIsMenuOpen(false);
                            setIsFlagModalOpen(true);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-300"
                        >
                          <span className="text-lg">🏴</span>
                          <span className="text-sm font-medium">Flag User</span>
                        </button>
                      )}

                      {/* Boost User */}
                      {vectordbUserId && (
                        <button
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setIsMenuOpen(false);
                            setIsBoostModalOpen(true);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-300"
                        >
                          <span className="text-lg">⚡</span>
                          <span className="text-sm font-medium">Boost User</span>
                        </button>
                      )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <div className="text-right">
                <span className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-500 dark:text-gray-400 text-base">
                  {conversation.phoneNumber}
                </span>
              </div>
            </div>
          </div>

          {/* Messages Container */}
          <div 
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto p-5 bg-white dark:bg-gray-900 bg-opacity-30 relative"
            onScroll={handleMessageScroll}
            data-messages-area
          >
            {/* Auto-scroll indicator - shows when smooth scrolling to bottom */}
            <AnimatePresence>
              {isAutoScrolling && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                  className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 pointer-events-none"
                >
                  <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500 text-white rounded-full shadow-lg">
                    <motion.svg 
                      className="w-4 h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      animate={{ y: [0, 3, 0] }}
                      transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </motion.svg>
                    <span className="text-sm font-medium">Scrolling to latest...</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="space-y-1">
              {/* Unread pill when not at bottom and there are new messages */}
              {isActiveConversation && !isAtBottom() && conversation.messages?.length > 0 && (
                <div className="sticky top-2 z-10 flex justify-center mb-2">
                  <button
                    className="px-3 py-1 rounded-full bg-blue-600 text-white text-xs shadow hover:bg-blue-700"
                    onClick={() => scrollToBottom(true)}
                    title="Jump to latest"
                  >
                    New messages
                  </button>
                </div>
              )}
              {conversation.messages.map((message) => {
                // For group chats, label up to two human participants distinctly; AI uses isOwn=true styling
                let senderLabel: string | undefined;
                if (conversation.isGroup && !message.isOwn) {
                  // Pick name if available, else fallback to senderId
                  senderLabel = message.senderName || (message.senderId ? `User ${message.senderId}` : undefined);
                }
                return (
                  <div key={message.id} data-message-id={message.id}>
                    <MessageBubble
                      message={message}
                      onOpenMetadata={openMetadata}
                      senderLabel={senderLabel}
                      onOpenReactionMenu={handleOpenReactionMenu}
                    />
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Scroll to bottom button for this specific conversation */}
            {isActiveConversation && showScrollToBottom && (
              <button
                onClick={() => scrollToBottom()}
                className="absolute bottom-4 right-4 w-10 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-10"
                title="Scroll to latest message"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </button>
            )}
          </div>

          {/* Message Input - Only enabled for active conversation */}
          <div className="flex-shrink-0">
            <MessageInput 
              onSendMessage={handleSendMessage} 
              disabled={!isActiveConversation}
              isActiveConversation={isActiveConversation}
              onConversationClick={onConversationClick}
              conversationId={conversation.id}
              onTypingChange={isActiveConversation ? onTypingChange : undefined}
            />
          </div>
        </div>
      </div>
      {/* Metadata Dialog */}
      <Dialog open={isMetadataOpen} onOpenChange={setIsMetadataOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-white dark:bg-gray-900 backdrop-blur-xl bg-opacity-100">
          <DialogHeader>
            <DialogTitle>Tool Call Details</DialogTitle>
            <DialogDescription>Agent metadata for this message</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            {activeMetadata && <MetadataViewer metadata={activeMetadata} />}
          </div>
        </DialogContent>
      </Dialog>

      <ReactionMenu
        context={reactionMenu}
        sending={reactionSending}
        error={reactionError}
        onClose={() => setReactionMenu(null)}
        onSelect={handleSendReaction}
        menuRef={reactionMenuRef}
      />
      
      {/* Flag Modal */}
      {vectordbUserId && (
        <FlagModal
          isOpen={isFlagModalOpen}
          onClose={() => setIsFlagModalOpen(false)}
          userId={vectordbUserId}
          userName={humanParticipant?.name || conversation.name}
          conversationId={parseInt(conversation.id, 10)}
        />
      )}
      
      {/* Boost Modal */}
      {vectordbUserId && (
        <BoostModal
          isOpen={isBoostModalOpen}
          onClose={() => setIsBoostModalOpen(false)}
          userId={vectordbUserId}
          userName={humanParticipant?.name || conversation.name}
        />
      )}
      
      {/* AI Analysis Modal */}
      <AIAnalysisModal
        isOpen={isAIAnalysisModalOpen}
        onClose={() => setIsAIAnalysisModalOpen(false)}
        conversationId={parseInt(conversation.id)}
        conversationName={conversation.name}
      />
    </div>
  );
}

export const IntegratedConversationItem = React.memo(IntegratedConversationItemInner);